<template>
    <div class="d-flex align-items-center" style="max-width: 100%">
        <img :src="getDeliveryIntegrationLogo(delivery.method)" class="source-image mr-2 flex-shrink-0" :alt="delivery.method_label" :title="delivery.method_label" />
        <div class="d-flex flex-column" style="overflow: hidden">
            <template v-if="delivery.company_number">
                <span class="text-truncate text-body" :title="delivery.status_label">
                    {{ delivery.status_label || '—' }}
                </span>
                <div
                    class="delivery-panel d-flex"
                    v-click-outside="closeMenu"
                    @click="toggleMenu($event)"
                >
                    <div class="delivery-panel_number">{{ delivery.company_number }}</div>
                    <div class="delivery-panel_icon">
                        <i
                            class="icon-barcode2"
                            :class="{'text-success': delivery.sticker_file}"
                            :title="$t('labels.sticker_zebra')"
                        ></i>
                    </div>
                    <div class="delivery-panel_icon">
                        <i
                            class="icon-file-text2"
                            :class="{'text-success': delivery.waybill_file}"
                            :title="$t('labels.express_waybill')"
                        ></i>
                    </div>
                    <div
                        v-if="isMenuOpen"
                        class="dropdown-menu delivery-panel-menu"
                        :style="menuStyle"
                    >
                        <a class="dropdown-item" @click.stop="copyCompanyNumber()">
                            <i class="icon-copy mr-2"></i>{{ $t('buttons.copy') }}
                        </a>
                        <AppDropDownMenuSubmenu>
                            <a class="dropdown-item" href="#" @click.stop.prevent>
                                <i class="icon-truck mr-2"></i>{{ $t('labels.courier_menu') }}
                            </a>
                            <div class="dropdown-menu">
                                <AppDropDownMenuSubmenu>
                                    <a class="dropdown-item" href="#" @click.stop.prevent>
                                        <i class="icon-download mr-2"></i>{{ $t('labels.download') }}
                                    </a>
                                    <div class="dropdown-menu">
                                        <a
                                            class="dropdown-item"
                                            :class="{ disabled: !delivery.sticker_file, 'text-muted': !delivery.sticker_file }"
                                            @click.stop="delivery.sticker_file && openUrl(delivery.sticker_file.url)"
                                        >
                                            <i class="icon-barcode2 mr-2"></i>{{ $t('labels.sticker_zebra') }}
                                        </a>
                                        <a
                                            class="dropdown-item"
                                            :class="{ disabled: !delivery.waybill_file, 'text-muted': !delivery.waybill_file }"
                                            @click.stop="delivery.waybill_file && openUrl(delivery.waybill_file.url)"
                                        >
                                            <i class="icon-file-text2 mr-2"></i>{{ $t('labels.express_waybill') }}
                                        </a>
                                    </div>
                                </AppDropDownMenuSubmenu>
                            </div>
                        </AppDropDownMenuSubmenu>
                    </div>
                </div>
            </template>
            <template v-else>
                <span class="text-truncate text-body">
                    {{ delivery.address }}
                </span>
                <span
                    class="text-smaller text-teal cursor-pointer"
                    v-if="canCreateNovapostWaybill || canCreateRozetkaWaybill"
                    @click.prevent="showCreateWaybillModal()"
                >
                    + {{ $t('buttons.create_waybill') }}
                </span>
            </template>
        </div>
    </div>
    <CreateWaybillModal
        ref="CreateWaybillModal"
        v-if="canCreateNovapostWaybill"
        :method="delivery.method"
        :address="delivery.address ?? ''"
        :recipient-name="delivery.recipient_name || order?.client?.name || ''"
        :recipient-phone="delivery.recipient_phone || order?.client?.phone || ''"
        :order-sum="order?.sum ?? 0"
        :external-order-number="order?.external_order_number ?? ''"
        :order-uuid="order.uuid"
        :delivery-uuid="delivery.uuid"
        @created="$emit('created', $event)"
    />
    <CreateRozetkaWaybillModal
        ref="CreateRozetkaWaybillModal"
        v-if="canCreateRozetkaWaybill"
        :recipient-name="delivery.recipient_name || order?.client?.name || ''"
        :recipient-phone="delivery.recipient_phone || order?.client?.phone || ''"
        :order-sum="order?.sum ?? 0"
        :order-uuid="order.uuid"
        :integration-uuid="order.integration_order.integration.uuid"
        :delivery-uuid="delivery.uuid"
        @created="$emit('created', $event)"
    />
</template>
<script>
import AppDropDownMenuSubmenu from "../../../../components/dropdown/AppDropDownMenuSubmenu.vue";
import CreateWaybillModal from "../sidebarCards/modals/CreateWaybillModal.vue";
import CreateRozetkaWaybillModal from "../sidebarCards/modals/CreateRozetkaWaybillModal.vue";

import { DeliveryIntegrationsEnum, getDeliveryIntegrationLogo } from "../../../../enums/DeliveryIntegrationsEnum";
import { IntegrationServiceEnum } from "../../../../enums/IntegrationServiceEnum";

export default {
    name: 'OrderDeliveryCell',
    components: { AppDropDownMenuSubmenu, CreateWaybillModal, CreateRozetkaWaybillModal },
    props: {
        delivery: {
            type: Object,
            required: true
        },
        order: {
            type: Object,
            required: true
        }
    },
    emits: ['created'],
    data() {
        return {
            getDeliveryIntegrationLogo,
            isMenuOpen: false,
            menuStyle: {}
        }
    },
    mounted() {
        this.menuAnchor = null;
    },
    unmounted() {
        this.stopTrackingMenuPosition();
    },
    methods: {
        toggleMenu(event) {
            if (this.isMenuOpen) {
                this.closeMenu();
                return;
            }

            this.menuAnchor = event.currentTarget;
            this.updateMenuPosition();
            this.isMenuOpen = true;

            window.addEventListener('scroll', this.updateMenuPosition, true);
            window.addEventListener('resize', this.updateMenuPosition);
        },
        closeMenu() {
            if (!this.isMenuOpen) {
                return;
            }

            this.isMenuOpen = false;
            this.menuAnchor = null;

            this.stopTrackingMenuPosition();
        },
        stopTrackingMenuPosition() {
            window.removeEventListener('scroll', this.updateMenuPosition, true);
            window.removeEventListener('resize', this.updateMenuPosition);
        },
        updateMenuPosition() {
            if (!this.menuAnchor) {
                return;
            }

            const rect = this.menuAnchor.getBoundingClientRect();

            this.menuStyle = {
                position: 'fixed',
                top: `${rect.bottom}px`,
                left: `${rect.left}px`,
                zIndex: 1000
            };
        },
        copyCompanyNumber() {
            navigator.clipboard.writeText(this.delivery.company_number);
            this.closeMenu();
        },
        openUrl(url) {
            window.open(url, '_blank');
        },
        showCreateWaybillModal() {
            if (this.canCreateRozetkaWaybill) {
                this.$refs.CreateRozetkaWaybillModal.show();

                return;
            }

            this.$refs.CreateWaybillModal.show();
        }
    },
    computed: {
        canCreateNovapostWaybill() {
            let canCreate = !this.delivery.company_number
                && this.delivery.method === DeliveryIntegrationsEnum.NOVAPOST;

            return canCreate;
        },
        canCreateRozetkaWaybill() {
            let canCreate = !this.delivery.company_number
                && this.delivery.method === DeliveryIntegrationsEnum.ROZETKA
                && this.order.integration_order?.integration?.service === IntegrationServiceEnum.ROZETKA;

            return canCreate;
        }
    }
}
</script>
<style>
.source-image {
    border-radius: 5px;
    object-fit: cover;
    object-position: center;
    width: 20px;
    height: 20px;
}
.text-smaller {
    font-size: 12px;
    line-height: 18px;
}
</style>
<style scoped>
.delivery-panel {
    position: relative;
    align-self: flex-start;
    border: 1px solid #eaeaea;
    border-radius: .375rem;
    padding: 0px 10px;
    gap: 10px;
    background: #f8f8f8;
}
.delivery-panel:hover {
    background: #eeeeee;
    cursor: pointer;
}
.delivery-panel-menu {
    display: block;
}
.delivery-panel_number {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.delivery-panel > .delivery-panel_icon {
    color: #999;
}
.delivery-panel > .delivery-panel_icon > i {
    font-size: 10px;
}
</style>
